import React from "react";
import { Button, Dropdown, OverlayTrigger, Tooltip, Spinner } from "react-bootstrap";

/**
 * The appointment's note list — the editor's left column.
 *
 * This column used to switch SECTIONS of a single note (Template / Note Pad /
 * Write / Media). Now that one session document is one note of one kind, it
 * switches NOTES instead, and those four labels became the kinds you can add.
 *
 * Recordings waiting to be written up are NOT here: they live in the Review
 * Needed / Dismissed buttons above the editor (CaptureReviewBar). A rail row is
 * a note that exists; a capture is work that hasn't become one yet, and mixing
 * them meant two surfaces listing the same recordings and disagreeing whenever
 * one went stale.
 *
 * Purely presentational: Session.jsx owns the notes list and every handler.
 */

const KINDS = [
  { key: "template", label: "Template", icon: "fa-light fa-file-waveform" },
  { key: "note", label: "Note", icon: "fa-light fa-clipboard-medical" },
  { key: "write", label: "Write", icon: "fa-light fa-pen-nib" },
  { key: "media", label: "Media", icon: "fa-light fa-camera" },
];

const ICON_BY_KIND = KINDS.reduce((acc, k) => ({ ...acc, [k.key]: k.icon }), {});

/** Icon for a note's kind, falling back to the generic note glyph. */
export function iconForKind(kind) {
  return ICON_BY_KIND[kind] || "fa-light fa-clipboard-medical";
}

const KIND_LABELS = {
  template: "Template",
  note: "Note",
  write: "Handwritten",
  media: "Media",
};

/**
 * A note's kind, derived from its content when the document doesn't carry one.
 *
 * Mirrors deriveSessionType in backend/src/services/sessionNoteService.js — the
 * list endpoint derives it server-side, but anywhere reading raw session
 * documents (Previous Notes) has to do the same, and both must agree or a note
 * opens on the wrong editor.
 */
export function deriveKind(s) {
  if (s?.sessionType) return s.sessionType;
  if (s?.capturedValues && Object.keys(s.capturedValues).length) return "template";
  if (Array.isArray(s?.sections) && s.sections.length) return "template";
  if (s?.sessionsTemplateId) return "template";
  if (s?.notepadContent) return "note";
  if (s?.handwritingImage) return "write";
  if (Array.isArray(s?.mediaFiles) && s.mediaFiles.length) return "media";
  return "note";
}

/** True when a document predates the split and holds more than one kind. */
export function isLegacyBundle(s) {
  if (s?.sessionType) return false;
  const kinds = [
    (s?.capturedValues && Object.keys(s.capturedValues).length) ||
      (Array.isArray(s?.sections) && s.sections.length) ||
      !!s?.sessionsTemplateId,
    !!s?.notepadContent,
    !!s?.handwritingImage,
    Array.isArray(s?.mediaFiles) && s.mediaFiles.length > 0,
  ];
  return kinds.filter(Boolean).length > 1;
}

/**
 * Every kind of content a document actually holds. Mirrors contentKindsOf in
 * backend/src/services/sessionNoteService.js.
 */
export function contentKindsOf(s) {
  const kinds = [];
  if (
    (s?.capturedValues && Object.keys(s.capturedValues).length) ||
    (Array.isArray(s?.sections) && s.sections.length) ||
    s?.sessionsTemplateId
  ) kinds.push("template");
  if (s?.notepadContent) kinds.push("note");
  if (s?.handwritingImage) kinds.push("write");
  if (Array.isArray(s?.mediaFiles) && s.mediaFiles.length) kinds.push("media");
  return kinds;
}

/**
 * Rail rows for one saved note.
 *
 * A legacy bundle — written before one-note-one-kind, holding e.g. a template
 * AND a handwritten page — becomes one row PER kind it holds, so both are
 * reachable and neither disappears when the other is opened. Each row still
 * points at the same document via `realId`; nothing is split in the database.
 *
 * @param row  either a list-endpoint row (carries `contentKinds`) or a full
 *             session document (kinds derived locally).
 */
export function expandNoteRows(row, { title } = {}) {
  const kinds = Array.isArray(row?.contentKinds) && row.contentKinds.length
    ? row.contentKinds
    : contentKindsOf(row);
  const name = title || noteTitleOf(row);
  const id = String(row._id);

  // Written after the split, or holding at most one kind — a single row.
  if (row?.sessionType || kinds.length <= 1) {
    return [{
      ...row,
      _id: id,
      realId: id,
      sessionType: row?.sessionType || kinds[0] || deriveKind(row),
      sessionsNoteTitle: name,
      isSplit: false,
    }];
  }

  return kinds.map((kind) => ({
    ...row,
    // Synthetic id: the rail needs distinct keys, the editor uses `realId`.
    _id: `${id}#${kind}`,
    realId: id,
    sessionType: kind,
    sessionsNoteTitle: KIND_LABELS[kind] || name,
    isSplit: true,
  }));
}

/** Display name for a note: stored title, else its kind. */
export function noteTitleOf(s) {
  const stored = typeof s?.sessionsNoteTitle === "string" ? s.sessionsNoteTitle.trim() : "";
  if (stored) return stored;
  return KIND_LABELS[deriveKind(s)] || KIND_LABELS.note;
}

/**
 * "Add" — the kind picker for a new note. Lives in the toolbar above the editor
 * on the appointment view, and inside the rail everywhere else.
 */
export function AddNoteDropdown({
  onAdd,
  disabled = false,
  isMobile = false,
  className = "",
  // Optional fixed width, so Add can be sized to match SyncTo beside it.
  width,
  // Render as a plain clickable label rather than a bordered button. In the
  // rail the notes themselves are the buttons; a third bordered box under them
  // competed with the list it belongs to instead of trailing it.
  asLink = false,
  // Entries to offer above the note kinds — currently SyncTo, so recording is
  // one of the ways to start a note rather than a separate button beside the
  // menu that starts them.
  leadingItems = null,
}) {
  return (
    <Dropdown className={className} style={width && !asLink ? { width } : undefined}>
      <Dropdown.Toggle
        variant={asLink ? "link" : "outline-primary"}
        className={
          asLink
            ? "text-primary text-decoration-none border-0 p-0 ps-1 d-flex align-items-center gap-2 fw-semibold shadow-none"
            : "rounded-0 d-flex align-items-center gap-2 justify-content-center w-100"
        }
        disabled={disabled}
        id="add-note-toggle"
      >
        <i className="fa-solid fa-plus" />
        {/* A label with no label is just a plus sign — the text stays even on
            mobile, where the bordered variant drops it to save room. */}
        {(asLink || !isMobile) && <span>Add</span>}
      </Dropdown.Toggle>
      <Dropdown.Menu className="shadow-sm">
        {leadingItems}
        {leadingItems && <Dropdown.Divider />}
        {KINDS.map((k) => (
          <Dropdown.Item key={k.key} onClick={() => onAdd?.(k.key)}>
            <i className={`${k.icon} me-2`} />
            {k.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default function SessionNoteRail({
  notes = [],
  // Forwarded to Add's menu — see AddNoteDropdown.leadingItems.
  addLeadingItems = null,
  activeNoteId,
  onSelect,
  onAdd,
  onDelete,
  busy = false,
  isMobile = false,
  disabled = false,
  // The appointment view renders Add in the toolbar above the editor instead,
  // so the rail is purely the list of notes there.
  showAdd = true,
}) {
  // Selected row uses the brand tint (--acc-brand-tint) rather than the solid
  // primary fill — the same light blue as the nav bar. A column of solid-blue
  // rows read far heavier than a note list should.
  const rowStyle = (isActive) => ({
    backgroundColor: isActive ? "#cfe2ff" : "#fff",
    borderColor: isActive ? "#6eaaee" : "#dee2e6",
    color: "#0f3490",
    fontWeight: isActive ? 600 : "normal",
  });

  return (
    <div className="d-flex flex-column gap-1 w-100">
      {/* ── The notes themselves ────────────────────────────────────────── */}
      {notes.length === 0 && !busy && (
        <div className="text-muted small px-1 py-2">No notes yet.</div>
      )}
      {busy && notes.length === 0 && (
        <div className="px-1 py-2">
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      )}

      {notes.map((n) => {
        const isActive = String(n._id) === String(activeNoteId);
        return (
          <div key={n._id} className="d-flex align-items-stretch gap-1">
            <Button
              variant=""
              className="rounded-0 d-flex align-items-center gap-2 text-start flex-grow-1"
              style={{ ...rowStyle(isActive), minWidth: 0 }}
              onClick={() => onSelect?.(n)}
              title={n.sessionsNoteTitle}
            >
              <i className={iconForKind(n.sessionType)} />
              {/* Shown on mobile too. Four kinds share three similar glyphs, so
                  an icon-only rail asked the user to tell a template from a
                  note by shape alone. It truncates instead — the parent caps
                  the rail's width on a narrow screen. */}
              <span className="text-truncate" style={{ minWidth: 0 }}>
                {n.sessionsNoteTitle}
              </span>
              {/* An unsaved note is the one thing worth calling out inline —
                  everything else about a note is visible once it is open. */}
              {!isMobile && n.isDraft && (
                <span className="ms-auto small fst-italic">draft</span>
              )}
            </Button>
            {onDelete && !isMobile && !n.isSplit && (
              <OverlayTrigger placement="right" overlay={<Tooltip>Delete note</Tooltip>}>
                <Button
                  variant="link"
                  className="text-danger p-0 px-1 border-0"
                  aria-label={`Delete ${n.sessionsNoteTitle}`}
                  onClick={() => onDelete(n)}
                >
                  <i className="fa-solid fa-trash" style={{ fontSize: "0.75rem" }} />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        );
      })}

      {/* ── Add ──────────────────────────────────────────────────────────
          Below the last note, as a plain clickable label. It reads as the next
          line of the list — "…and add another" — rather than as a control
          sitting above it competing with the notes for attention. */}
      {showAdd && (
        <div className="mt-1">
          <AddNoteDropdown
            onAdd={onAdd}
            disabled={disabled}
            isMobile={isMobile}
            asLink
            leadingItems={addLeadingItems}
          />
        </div>
      )}
    </div>
  );
}

export { KINDS };
